import { DAY_ORDER, parseWeeklySchedule, WeeklySchedule } from "./scheduleUtils";

export interface MenuData {
  menuId: string;
  menuName: string;
  name_for_owner?: string;
  name_for_customer?: string;
  name_for_bartender?: string;
  enabled?: boolean;
  use_schedule?: boolean;
  active_schedule: WeeklySchedule;
  visible_schedule: WeeklySchedule;
  active_date_ranges_whitelist?: unknown[];
  active_date_ranges_blacklist?: unknown[];
  visible_date_ranges_whitelist?: unknown[];
  visible_date_ranges_blacklist?: unknown[];
  allow_order_ahead?: boolean;
  min_order_ahead_minutes?: number;
  min_order_ahead_days?: number;
  max_order_ahead_minutes?: number;
  combine_into_id?: string | null;
  timepicker_title?: string;
  timepicker_block_interval?: number;
  timepicker_block_width?: number;
  show_out_of_stock_items?: boolean;
  show_unfulfillable_items?: string;
  display_position?: number;
}

export interface MenuPayload {
  menuId: string;
  menuName: string;
  enabled?: boolean;
  use_schedule: boolean;
  active_schedule: WeeklySchedule;
  visible_schedule: WeeklySchedule;
  active_date_ranges_whitelist?: unknown[];
  active_date_ranges_blacklist?: unknown[];
  visible_date_ranges_whitelist?: unknown[];
  visible_date_ranges_blacklist?: unknown[];
  allow_order_ahead?: boolean;
  min_order_ahead_minutes?: number;
  min_order_ahead_days?: number;
  max_order_ahead_minutes?: number;
  combine_into_id?: string | null;
  timepicker_title?: string;
  timepicker_block_interval?: number;
  timepicker_block_width?: number;
  show_out_of_stock_items?: boolean;
  show_unfulfillable_items?: string;
}

export interface BuildResult {
  error?: string;
  payload?: MenuPayload;
}

const everyDayHasInput = (schedule: WeeklySchedule): boolean =>
  DAY_ORDER.every((day) => Boolean(schedule?.[day]?.customer_input));

// Port of the legacy updateMenuSchedule validation + payload assembly: validate
// the order-ahead numbers and both weekly schedules, then return either an error
// message (surfaced as a toast, matching the old page) or the fields to POST to
// updateMenuProperties. Date-range lists are passed through unchanged; their
// editor lands in a follow-up change. Only real Menu fields are sent (the legacy
// page posted the whole scope object, including client-only cruft).
export const buildMenuPayload = (menu: MenuData, ordersSchedule: string): BuildResult => {
  const maxMinutes = menu.max_order_ahead_minutes;
  if (!maxMinutes) {
    return { error: "Guests can only schedule orders less than 2040 hours in the future." };
  }
  const minMinutes = menu.min_order_ahead_minutes;
  if (minMinutes == null || minMinutes < 0) {
    return { error: "Your Shortest Delivery/Pickup Time must be at least 0 minutes." };
  }
  const blockInterval = menu.timepicker_block_interval;
  if (!blockInterval || blockInterval < 10) {
    return { error: "Your Timeblock Spacing must be at least 10." };
  }
  const minDays = menu.min_order_ahead_days ?? 0;
  if (minMinutes >= maxMinutes) {
    return {
      error:
        "You must decrease the shortest delivery/pickup time or increase how far in the future guests can place orders.",
    };
  }
  if (maxMinutes <= minDays * 60 * 24) {
    return {
      error:
        "You must decrease the number of days guests must order ahead or increase how far in the future guests can place orders.",
    };
  }
  if (!everyDayHasInput(menu.active_schedule) || !everyDayHasInput(menu.visible_schedule)) {
    return { error: "Please check your inputs. You must enter times in each one." };
  }

  const useSchedule = ordersSchedule === "useschedule" || Boolean(menu.allow_order_ahead);
  const activeParsed = parseWeeklySchedule(menu.active_schedule);
  const visibleParsed = parseWeeklySchedule(menu.visible_schedule);
  if (!activeParsed.userScheduleIsValid) {
    return { error: "Your Allowed Delivery/Pickup Times are not valid. Please see the examples." };
  }
  if (!visibleParsed.userScheduleIsValid) {
    return { error: "Your Allowed Ordering Window Times are not valid. Please see the examples." };
  }

  return {
    payload: {
      menuId: menu.menuId,
      menuName: menu.menuName,
      enabled: menu.enabled,
      use_schedule: useSchedule,
      active_schedule: activeParsed.scheduleAsMilitaryTime,
      visible_schedule: visibleParsed.scheduleAsMilitaryTime,
      active_date_ranges_whitelist: menu.active_date_ranges_whitelist,
      active_date_ranges_blacklist: menu.active_date_ranges_blacklist,
      visible_date_ranges_whitelist: menu.visible_date_ranges_whitelist,
      visible_date_ranges_blacklist: menu.visible_date_ranges_blacklist,
      allow_order_ahead: menu.allow_order_ahead,
      min_order_ahead_minutes: menu.min_order_ahead_minutes,
      min_order_ahead_days: menu.min_order_ahead_days,
      max_order_ahead_minutes: menu.max_order_ahead_minutes,
      combine_into_id: menu.combine_into_id,
      timepicker_title: menu.timepicker_title,
      timepicker_block_interval: menu.timepicker_block_interval,
      timepicker_block_width: menu.timepicker_block_width,
      show_out_of_stock_items: menu.show_out_of_stock_items,
      show_unfulfillable_items: menu.show_unfulfillable_items,
    },
  };
};

// Legacy determineRadioValueHelper, minus the dead misspelled use_visibile_schedule
// branch: a menu is "on a schedule" when it uses one or accepts future orders.
export const initialOrdersSchedule = (menu: MenuData): string =>
  menu.use_schedule || menu.allow_order_ahead ? "useschedule" : "inheritfromprepstation";
